// MorningBrew.jsx — the "this morning" hero (Apple design language).
//
// Models Josh's real morning flow: the open bag + how many cups → the full
// recipe (dose, Ode grind, Fellow-app profile), with no digging through the
// bean library first. Two engines, toggled at the top:
//   - "Calculated" (default): deterministic Aiden calculator, biased by the
//     learned taste profile. Instant, offline, same recipe every day.
//   - "AI dial-in": suggestMorningBrew — a per-bean suggestion. Needs the API
//     key; costs a call; varies with intent.
// One tap finishes it: Push to Aiden, or Copy the values for the Fellow app.
import { useState, useMemo, useEffect } from "react";
import { Sparkles, Loader2, Copy, Check, UploadCloud, Wand2, X, ChevronRight, Pencil } from "lucide-react";
import { calculateAidenBrew } from "../lib/coffee";
import { applyTasteToBrew } from "../lib/taste-profile";
import { cupsToWaterMl, findActiveBags, getOpenBeans, forecastDepletion } from "../lib/brew-helpers";
import { suggestMorningBrew } from "../lib/ai";
import { pushProfileToAiden } from "../lib/aiden";
import { Segmented, SpecTiles, Stepper, PrimaryButton, IconButton, Sheet } from "./ios";
import { buildBrewFromRecipe } from "../lib/bean-import";

const cToF = (c) => Math.round((+c || 0) * 9 / 5 + 32);
const fToC = (f) => (+f - 32) * 5 / 9;
const clampC = (c) => Math.max(50, Math.min(99, Math.round(c)));
const fmtCups = (c) => (Number.isInteger(c) ? `${c}` : c.toFixed(1));

// Map a morning mode to an Aiden base profile id.
function profileForMode(mode, fallback) {
  if (mode === "cold") return "cold-brew";
  if (mode === "iced") return "iced";
  return fallback || "balanced";
}

// Shared deterministic compute — reused by the Daily preview so the two
// surfaces never drift.
export function computeMorningBrew({ bean, cups, mode = "hot", tasteProfile = null, defaultProfileId = "balanced" }) {
  if (!bean) return null;
  const base = calculateAidenBrew({
    bean,
    profileId: profileForMode(mode, defaultProfileId),
    waterMl: cupsToWaterMl(cups),
  });
  return applyTasteToBrew(base, tasteProfile);
}

const parseRatio = (s) => { const m = String(s || "").match(/(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)/); return m ? +m[2] / +m[1] : null; };

// The bag to open by default — modeling the single bag actually in Josh's
// vacuum container. Priority:
//   1. The bag he last brewed/pushed (persisted as preferences.last_bean_id).
//   2. The most recently brewed bag (from brew logs).
//   3. The most recently added open bag, else the first open bag.
function smartDefaultBeanId(store) {
  const beans = store.beans || [];
  if (!beans.length) return null;
  const open = getOpenBeans(beans, store.preferences);
  // Only one bag open → it's unambiguously the one in the container.
  if (open.length === 1) return open[0].id;
  const lastId = store.preferences?.last_bean_id;
  if (lastId && open.some(b => b.id === lastId)) return lastId;
  const logs = [...(store.brewLogs || [])].sort((a, b) => new Date(b.brewed_at) - new Date(a.brewed_at));
  const lastBrewedOpen = logs.find(l => open.some(b => b.id === l.bean_id));
  if (lastBrewedOpen) return lastBrewedOpen.bean_id;
  if (lastId && beans.some(b => b.id === lastId)) return lastId;
  const pool = open.length ? open : beans;
  const newest = [...pool].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
  return newest?.id || pool[0].id;
}

const isSameDay = (d) => {
  const x = new Date(d), n = new Date();
  return x.getFullYear() === n.getFullYear() && x.getMonth() === n.getMonth() && x.getDate() === n.getDate();
};

const TUNES = [
  { id: "none", label: "Just right" },
  { id: "bitter", label: "A bit bitter" },
  { id: "flavor", label: "More flavor" },
  { id: "stomach", label: "Easier on stomach" },
];

// One-tap morning nudge from yesterday's cup. Adjusts grind / temp / bloom on
// both the readout and the pushable Aiden profile.
function applyTune(brew, tune) {
  if (!brew || tune === "none") return brew;
  const delta = {
    bitter: { grind: +0.3, tempF: -2, bloomS: -5, note: "coarser + a touch cooler to cut bitterness" },
    flavor: { grind: -0.2, tempF: 0, bloomS: +5, note: "finer + a longer bloom for more sweetness" },
    stomach: { grind: +0.4, tempF: -3, bloomS: 0, note: "lower extraction — coarser + cooler" },
  }[tune];
  if (!delta) return brew;
  const clampC = (c) => Math.max(50, Math.min(99, Math.round(c)));
  const b = { ...brew, grind: { ...brew.grind }, aidenProfile: { ...brew.aidenProfile } };
  b.grind.setting = +(+b.grind.setting + delta.grind).toFixed(1);
  if (delta.tempF) {
    b.temp_f = brew.temp_f + delta.tempF;
    b.temp_c = Math.round((b.temp_f - 32) * 5 / 9);
    const dc = delta.tempF * 5 / 9;
    const shift = (arr) => (arr || []).map(t => clampC(t + dc));
    b.aidenProfile.ssPulseTemperatures = shift(b.aidenProfile.ssPulseTemperatures);
    b.aidenProfile.batchPulseTemperatures = shift(b.aidenProfile.batchPulseTemperatures);
    if (b.aidenProfile.bloomEnabled) b.aidenProfile.bloomTemperature = clampC((b.aidenProfile.bloomTemperature || 0) + dc);
  }
  if (delta.bloomS && b.aidenProfile.bloomEnabled) {
    b.aidenProfile.bloomDuration = Math.max(0, Math.min(120, (b.aidenProfile.bloomDuration || 0) + delta.bloomS));
    b.bloom_time_s = b.aidenProfile.bloomDuration;
  }
  b.tune_note = delta.note;
  return b;
}

// Copyable Fellow-app value block for a deterministic brew, in Josh's order.
function formatBrewForFellow({ dose, grind, cups, tempF, ratioStr, bloomEnabled, bloomRatio, bloomTimeS, bloomTempF }) {
  const lines = [
    `Coffee: ${dose} g`,
    `Grinder (Ode): ${grind}`,
    `Cups: ${fmtCups(cups)}`,
    `Temperature: ${tempF}°F`,
    `Ratio: ${ratioStr}`,
    `Bloom: ${bloomEnabled ? "On" : "Off"}`,
  ];
  if (bloomEnabled) {
    lines.push(`Bloom Ratio: 1:${bloomRatio}`);
    lines.push(`Bloom Time: ${bloomTimeS} sec`);
    lines.push(`Bloom Temp: ${bloomTempF}°F`);
  }
  return lines.join("\n");
}

// Copyable value block for an AI dial-in proposal.
function formatProposalForFellow(p, cups) {
  return [
    `Coffee: ${p.dose_g} g`,
    p.grind_hint ? `Grinder (Ode): ${p.grind_hint}` : null,
    `Cups: ${fmtCups(cups)}`,
    p.temp_f != null ? `Temperature: ${p.temp_f}°F` : null,
    p.ratio != null ? `Ratio: 1:${p.ratio}` : null,
    p.bloom_duration_s != null ? `Bloom Time: ${p.bloom_duration_s} sec` : null,
    p.bloom_temp_f != null ? `Bloom Temp: ${p.bloom_temp_f}°F` : null,
  ].filter(Boolean).join("\n");
}

// Build a valid Aiden profile from an AI proposal so it can be pushed. Starts
// from the deterministic profile for the proposal's base + cups (correct pulse
// structure) and overrides temps/bloom with the AI's values.
function proposalToAidenProfile(proposal, bean, cups) {
  const base = calculateAidenBrew({
    bean,
    profileId: proposal.profile_id || "balanced",
    waterMl: cupsToWaterMl(cups),
    ratioOverride: proposal.ratio && proposal.ratio > 0 ? proposal.ratio : null,
  });
  const p = { ...base.aidenProfile };
  const tempC = proposal.temp_c != null ? proposal.temp_c
    : proposal.temp_f != null ? fToC(proposal.temp_f) : null;
  if (tempC != null) {
    p.ssPulseTemperatures = (p.ssPulseTemperatures || []).map(() => clampC(tempC));
    p.batchPulseTemperatures = (p.batchPulseTemperatures || []).map(() => clampC(tempC));
  }
  if (p.bloomEnabled) {
    if (proposal.bloom_duration_s != null) {
      p.bloomDuration = Math.max(0, Math.min(120, Math.round(proposal.bloom_duration_s)));
    }
    const bloomC = proposal.bloom_temp_f != null ? fToC(proposal.bloom_temp_f)
      : tempC != null ? tempC - 3 : null;
    if (bloomC != null) p.bloomTemperature = clampC(bloomC);
  }
  return p;
}

const MODES = [
  { id: "hot", label: "Hot" },
  { id: "iced", label: "Iced" },
  { id: "cold", label: "Cold brew" },
];

const AI_INTENTS = [
  { id: "smooth", label: "Smooth", icon: "🍫" },
  { id: "balanced", label: "Balanced", icon: "⚖️" },
  { id: "sweeter", label: "Sweeter", icon: "🍯" },
  { id: "lighter", label: "Lighter", icon: "🌿" },
];

// Big rounded-numeral readout: dose + grind. The Apple "workout summary" moment.
function Readout({ dose, grind }) {
  return (
    <div className="flex items-end justify-between">
      <div className="font-round font-bold text-[68px] leading-[0.82] tracking-[-0.04em] tabular text-ink-700">
        {dose}<span className="font-round font-semibold text-[26px] tracking-[-0.02em] text-warm-500">g</span>
      </div>
      <div className="text-right pb-1.5">
        <div className="font-sf text-[11px] font-semibold uppercase tracking-[0.03em] text-warm-500">Grind · Ode</div>
        <div className="font-round font-bold text-[30px] leading-none tracking-[-0.02em] text-ink-700 mt-1">{grind}</div>
      </div>
    </div>
  );
}

// A single full-width choice row inside the wizard.
function WizardOption({ active, onClick, children, sub }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left font-sf px-4 py-3.5 rounded-2xl border transition-colors ${
        active ? "bg-ink-700 text-white border-ink-700" : "bg-white text-ink-700 border-[var(--border-subtle)]"
      }`}
    >
      <div className="text-[15px] font-semibold">{children}</div>
      {sub && <div className={`text-[12px] mt-0.5 ${active ? "text-white/70" : "text-warm-500"}`}>{sub}</div>}
    </button>
  );
}

// Guided morning flow — the same choices as the inline hero (mode → bag → cups
// → yesterday's cup → recipe), one question at a time, ending in Push to Aiden.
// Drives the hero's own state, so choices made here persist to the hero too.
function MorningWizard({
  open, onClose, greeting, mode, onMode, bean, activeBags, onBean,
  cups, onCups, tune, onTune, brew, onPush, onCopy, copied, pushState,
}) {
  const steps = ["mode", activeBags.length > 1 ? "bean" : null, "cups", "tune", "result"].filter(Boolean);
  const [i, setI] = useState(0);
  useEffect(() => { if (open) setI(0); }, [open]);
  const step = steps[i];
  const isLast = i === steps.length - 1;
  const next = () => setI((x) => Math.min(steps.length - 1, x + 1));
  const back = () => setI((x) => Math.max(0, x - 1));
  // Tap an answer → set it, then auto-advance after a beat so the highlight registers.
  const choose = (fn) => (val) => { fn(val); window.setTimeout(next, 160); };

  const titles = {
    mode: "What are we brewing?",
    bean: "Which bag?",
    cups: "How many cups?",
    tune: "How did yesterday land?",
    result: "Here's your recipe",
  };

  const copyText = brew && formatBrewForFellow({
    dose: brew.dose_g, grind: brew.grind.setting, cups, tempF: brew.temp_f, ratioStr: brew.ratio_str,
    bloomEnabled: brew.aidenProfile.bloomEnabled, bloomRatio: brew.aidenProfile.bloomRatio,
    bloomTimeS: brew.aidenProfile.bloomDuration, bloomTempF: cToF(brew.aidenProfile.bloomTemperature),
  });

  return (
    <Sheet open={open} onClose={onClose}>
      <div className="px-5 pt-4 pb-2 flex items-start justify-between">
        <div>
          <div className="font-sf text-[11px] font-semibold uppercase tracking-[0.06em] text-ember-500">{greeting}</div>
          <div className="font-round font-bold text-[22px] tracking-[-0.02em] text-ink-700 mt-0.5">{titles[step]}</div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-warm-200 text-warm-600 grid place-items-center flex-shrink-0"><X size={16} /></button>
      </div>

      <div className="flex gap-1.5 px-5 pb-3.5">
        {steps.map((s, idx) => <div key={s} className={`h-1 rounded-full flex-1 ${idx <= i ? "bg-ember-500" : "bg-warm-300"}`} />)}
      </div>

      <div className="px-5 pb-4 overflow-y-auto">
        {step === "mode" && (
          <div className="space-y-2">
            {MODES.map(m => <WizardOption key={m.id} active={mode === m.id} onClick={() => choose(onMode)(m.id)}>{m.label}</WizardOption>)}
          </div>
        )}
        {step === "bean" && (
          <div className="space-y-2">
            {activeBags.map(b => <WizardOption key={b.id} active={bean?.id === b.id} onClick={() => choose(onBean)(b.id)} sub={b.roaster || undefined}>{b.name}</WizardOption>)}
          </div>
        )}
        {step === "cups" && (
          <div className="flex flex-col items-center py-6 gap-5">
            <div className="font-round font-bold text-[68px] leading-none text-ink-700 tabular">{fmtCups(cups)}</div>
            <Stepper value={cups} onChange={onCups} min={1} max={10} step={0.5} format={fmtCups} />
          </div>
        )}
        {step === "tune" && (
          <div className="space-y-2">
            {TUNES.map(t => <WizardOption key={t.id} active={tune === t.id} onClick={() => choose(onTune)(t.id)}>{t.label}</WizardOption>)}
          </div>
        )}
        {step === "result" && brew && (
          <div className="bg-white rounded-[22px] p-4 border border-[var(--border-subtle)]">
            <div className="font-sf font-semibold text-[15px] text-ink-700 mb-3 truncate">{bean?.name}</div>
            <Readout dose={brew.dose_g} grind={brew.grind.setting} />
            <SpecTiles
              className="mt-4"
              items={[
                { label: "Temp", value: `${brew.temp_f}°` },
                { label: "Ratio", value: brew.ratio_str },
                { label: "Cups", value: fmtCups(cups) },
                { label: "Bloom", value: `${brew.bloom_time_s}s` },
              ]}
            />
            {brew.tune_note && <div className="mt-3 text-[12.5px] text-ember-700 font-medium">Nudged {brew.tune_note}.</div>}
            {pushState.status === "done" && <div className="mt-3 text-[12px] text-sage-700">{pushState.message}</div>}
            {pushState.status === "error" && <div className="mt-3 text-[12px] text-ember-600">{pushState.message}</div>}
          </div>
        )}
      </div>

      <div className="px-5 py-4 border-t border-[var(--border-subtle)] flex items-center gap-2.5">
        {i > 0 && <button onClick={back} className="font-sf text-[14px] font-semibold text-ink-500 px-3 py-2.5">Back</button>}
        {step === "cups" ? (
          <PrimaryButton onClick={next} className="flex-1">Continue</PrimaryButton>
        ) : isLast ? (
          <>
            <PrimaryButton onClick={() => onPush(brew.aidenProfile)} disabled={pushState.status === "pushing"} className="flex-1">
              {pushState.status === "pushing"
                ? <><Loader2 size={16} className="animate-spin" /> Pushing…</>
                : <><UploadCloud size={16} /> Push to Aiden</>}
            </PrimaryButton>
            <IconButton onClick={() => onCopy(copyText)} title="Copy for the Fellow app">
              {copied ? <Check size={17} /> : <Copy size={17} />}
            </IconButton>
          </>
        ) : (
          <div className="flex-1 text-right font-sf text-[12px] text-warm-400">Tap to choose</div>
        )}
      </div>
    </Sheet>
  );
}

// How much is left in the container. pct + brews-left both come from
// forecastDepletion, which is driven by recorded brew logs, so "running low"
// reflects actual usage rather than a guess.
function BagGauge({ forecast }) {
  const pct = Math.max(0, Math.min(100, forecast.pct_remaining ?? 0));
  const low = forecast.should_reorder || pct <= 20;
  return (
    <div className="mb-3.5">
      <div className="flex items-center justify-between mb-1 px-0.5">
        <span className="font-sf text-[11px] font-medium text-warm-500">
          In the container · {forecast.weight_left_g != null ? `${Math.round(forecast.weight_left_g)}g` : `${pct}%`}
        </span>
        <span className={`font-sf text-[11px] font-semibold ${low ? "text-ember-600" : "text-warm-500"}`}>
          {low
            ? `Running low · ~${forecast.brews_remaining} left`
            : `~${forecast.brews_remaining} brews${forecast.days_remaining != null ? ` · ~${forecast.days_remaining}d` : ""}`}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-warm-200 overflow-hidden">
        <div className={`h-full rounded-full ${low ? "bg-ember-500" : "bg-sage-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function MorningBrew({ store, weatherSuggestion = null, checkInTarget = null, checkInBean = null, onRateYesterday, onOpenBean }) {
  const activeBags = useMemo(() => getOpenBeans(store.beans, store.preferences), [store.beans, store.preferences]);
  const hour = new Date().getHours();
  const isEvening = hour >= 17;
  const greeting = hour < 12 ? "Good morning" : isEvening ? "Tonight — cold brew prep" : "Afternoon brew";
  const [selectedBeanId, setSelectedBeanId] = useState(() => smartDefaultBeanId(store) || activeBags[0]?.id || store.beans[0]?.id || null);
  const [cups, setCups] = useState(() => store.preferences?.default_cups || 8);
  const [mode, setMode] = useState(() => (isEvening ? "cold" : "hot"));
  const [tune, setTune] = useState("none");
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [engine, setEngine] = useState("calc"); // "calc" | "ai"
  const [wizardOpen, setWizardOpen] = useState(false);

  const [intent, setIntent] = useState("balanced");
  const [proposal, setProposal] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const [copied, setCopied] = useState(false);
  const [pushState, setPushState] = useState({ status: "idle", message: "" });

  const bean = useMemo(
    () => store.beans.find(b => b.id === selectedBeanId) || activeBags[0] || store.beans[0] || null,
    [store.beans, selectedBeanId, activeBags]
  );

  // Keep the hero locked to an OPEN bag. The initial selection is picked once at
  // mount; if the open set later changes (Josh toggles a bag open/closed) and the
  // current pick is no longer open, snap back to the smart default so the hero
  // never shows a closed bag as "open".
  useEffect(() => {
    if (!activeBags.length) return;
    const openIds = new Set(activeBags.map(b => b.id));
    if (!selectedBeanId || !openIds.has(selectedBeanId)) {
      const next = smartDefaultBeanId(store);
      if (next && next !== selectedBeanId) setSelectedBeanId(next);
    }
  }, [activeBags, selectedBeanId]); // eslint-disable-line react-hooks/exhaustive-deps

  const brew = useMemo(() => {
    // Prefer the bean's dialed recipe (from import) for hot brews, so the
    // readout matches what Josh actually brews; scale the dose to the cup count.
    const r = mode === "hot" ? bean?.details?.best_recipe : null;
    let base;
    if (r && r.dose_g != null) {
      base = buildBrewFromRecipe(bean, r, bean.name);
      const rc = r.cups || 8;
      const scale = rc ? cups / rc : 1;
      const rn = parseRatio(r.ratio) || base.ratio || 17;
      base.dose_g = +(r.dose_g * scale).toFixed(1);
      base.water_g = Math.round(base.dose_g * rn);
      base.bloom_water_g = Math.round(base.dose_g * (base.aidenProfile?.bloomRatio || 2));
      if (r.temp_f != null) { base.temp_f = Math.round(r.temp_f); base.temp_c = Math.round((r.temp_f - 32) * 5 / 9); }
      base.ratio_str = `1:${rn}`;
      if (r.bloom_time_s != null) base.bloom_time_s = r.bloom_time_s;
    } else {
      base = computeMorningBrew({ bean, cups, mode, tasteProfile: store.tasteProfile, defaultProfileId: store.preferences?.default_brew_profile });
    }
    return applyTune(base, tune);
  }, [bean, cups, mode, tune, store.tasteProfile, store.preferences?.default_brew_profile]);

  const forecast = useMemo(() => (bean ? forecastDepletion(bean, store.brewLogs) : null), [bean, store.brewLogs]);

  const resetOutputs = () => {
    setProposal(null);
    setCopied(false);
    setPushState({ status: "idle", message: "" });
  };
  const changeCups = (v) => { setCups(v); resetOutputs(); };
  const changeMode = (m) => { setMode(m); resetOutputs(); };
  const changeTune = (t) => { setTune(t); resetOutputs(); };
  const selectBean = (id) => { setSelectedBeanId(id); resetOutputs(); };

  // Record that this bag is the one being brewed today. Keeps one log per bag
  // per day (updates rather than duplicating on repeated pushes), remembers the
  // bag as the open one, and seeds tomorrow's "rate yesterday's brew" prompt so
  // the taste profile keeps learning. Non-fatal — the brew still works if this
  // write fails.
  const recordBrewForToday = async () => {
    if (!bean || !brew) return;
    try {
      const existing = (store.brewLogs || []).find(l => l.bean_id === bean.id && isSameDay(l.brewed_at));
      const brewData = { ...brew, mode, cups };
      if (existing && store.updateBrewLog) {
        await store.updateBrewLog(existing.id, {
          dose_g: brew.dose_g, grind_setting: brew.grind?.setting, temp_c: brew.temp_c, brew_data: brewData,
        });
      } else if (store.logBrew) {
        await store.logBrew({
          bean_id: bean.id,
          profile: { id: mode },
          dose_g: brew.dose_g,
          water_g: brew.water_g,
          grind: brew.grind,
          temp_c: brew.temp_c,
          brew_data: brewData,
        });
      }
      if (store.setPreferences) {
        // Brewing a bag declares it open (in the container). If Josh has never
        // set the open list, brewing seeds it with just this bag.
        const prevOpen = store.preferences?.open_bean_ids;
        const open_bean_ids = Array.isArray(prevOpen)
          ? Array.from(new Set([...prevOpen, bean.id]))
          : [bean.id];
        await store.setPreferences({ ...store.preferences, open_bean_ids, last_bean_id: bean.id, last_brew_at: new Date().toISOString() });
      }
    } catch { /* non-fatal — logging is best-effort */ }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      recordBrewForToday();
    } catch { /* clipboard blocked — values are still on screen */ }
  };

  const handlePush = async (profile) => {
    setPushState({ status: "pushing", message: "" });
    try {
      const res = await pushProfileToAiden(profile);
      setPushState({ status: "done", message: res.message });
      recordBrewForToday();
    } catch (e) {
      setPushState({ status: "error", message: e.message || "Couldn't push to the Aiden" });
    }
  };

  const runAi = async () => {
    if (!store.apiKey || !bean) return;
    setAiLoading(true); setAiError(null);
    try {
      const beanLogs = store.brewLogs.filter(l => l.bean_id === bean.id).slice(0, 5);
      const out = await suggestMorningBrew({
        apiKey: store.apiKey, bean, intent, cups,
        recentLogs: beanLogs,
        tasteProfile: store.tasteProfile,
        customRules: store.preferences?.coffee_rules || null,
      });
      setProposal(out);
    } catch (e) {
      setAiError(e.message || "Couldn't get a suggestion");
    } finally {
      setAiLoading(false);
    }
  };

  // Final step — quiet, since pushing is the last thing you do after the recipe
  // is settled. Copy + Push sit as equal, low-key secondary buttons (cups moved
  // up into the flow).
  const actions = (text, profile) => (
    <div className="mt-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleCopy(text)}
          className="flex-1 h-10 rounded-full bg-warm-200 text-ink-600 font-sf text-[13px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
        >
          {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy for Fellow"}
        </button>
        <button
          onClick={() => handlePush(profile)}
          disabled={pushState.status === "pushing"}
          className="flex-1 h-10 rounded-full border border-ember-500/45 text-ember-600 bg-ember-500/[0.04] font-sf text-[13px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60 active:scale-[0.98] transition-transform"
        >
          {pushState.status === "pushing"
            ? <><Loader2 size={15} className="animate-spin" /> Pushing…</>
            : <><UploadCloud size={15} /> Push to Aiden</>}
        </button>
      </div>
      {pushState.status === "done" && <div className="mt-2 text-[12px] text-sage-700 px-1">{pushState.message}</div>}
      {pushState.status === "error" && <div className="mt-2 text-[12px] text-ember-600 px-1">{pushState.message}</div>}
    </div>
  );

  if (!bean) {
    return (
      <div className="bg-white rounded-[26px] p-5 border border-[var(--border-subtle)] mb-6 font-sf">
        <div className="font-sf text-[11px] font-semibold uppercase tracking-[0.04em] text-ember-500 mb-1">This morning</div>
        <div className="text-[14px] text-warm-500">No open bag yet — add a bean below and I'll dial in your morning recipe.</div>
      </div>
    );
  }

  const suggestCold = weatherSuggestion?.suggested_mode === "cold" && mode !== "cold";
  const suggestIced = weatherSuggestion?.suggested_mode === "iced" && mode !== "iced";

  return (
    <div className="bg-white rounded-[26px] p-[18px] pb-5 border border-[var(--border-subtle)] shadow-[0_8px_30px_-14px_rgba(35,25,15,0.28)] mb-6 font-sf">
      <div className="flex items-center justify-between mb-2.5">
        <div className="font-sf text-[11px] font-semibold uppercase tracking-[0.06em] text-ember-500">{greeting}</div>
        <button
          onClick={() => setWizardOpen(true)}
          className="font-sf text-[12px] font-semibold text-ember-600 flex items-center gap-1 bg-ember-500/10 rounded-full px-2.5 py-1"
        >
          <Wand2 size={12} /> Morning wizard
        </button>
      </div>
      {/* Bag header — tap to edit the bean (name + all settings). */}
      <button
        onClick={() => onOpenBean?.(bean.id)}
        className="w-full flex items-center gap-3 mb-3.5 text-left group"
      >
        <div
          className="w-[42px] h-[54px] rounded-[9px] relative overflow-hidden flex-shrink-0 shadow-[0_5px_12px_-4px_rgba(35,25,15,0.5)]"
          style={{ background: "linear-gradient(158deg,#946037,#3f2617)" }}
        >
          <div className="absolute inset-x-0 top-0 h-2.5 bg-black/25" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <div className="font-sf font-semibold text-[17px] tracking-[-0.01em] text-ink-700 truncate">{bean.name}</div>
            <Pencil size={12} className="text-warm-400 flex-shrink-0" />
          </div>
          <div className="text-[12.5px] text-warm-500 mt-px truncate">
            {bean.id === store.preferences?.last_bean_id ? "In your container" : "Open bag"}
            {bean.roaster && <> · {bean.roaster}</>}
          </div>
        </div>
      </button>

      {/* How much is left — a slim gauge that reads low based on recorded usage. */}
      {forecast && (
        <BagGauge forecast={forecast} />
      )}

      {/* Rate yesterday — the start of the flow. Feeds the taste profile. */}
      {checkInTarget && (
        <button
          onClick={() => onRateYesterday?.()}
          className="w-full flex items-center gap-2.5 bg-sage-500/10 rounded-2xl px-3.5 py-2.5 mb-3.5 text-left"
        >
          <span className="w-6 h-6 rounded-full bg-sage-500/20 grid place-items-center text-[13px] flex-shrink-0">☕️</span>
          <span className="flex-1 font-sf text-[13px] text-ink-600">How was yesterday's <b className="font-semibold text-ink-700">{checkInBean?.name || "brew"}</b>?</span>
          <span className="font-sf text-[12px] font-semibold text-sage-700">Rate</span>
          <ChevronRight size={14} className="text-sage-600" />
        </button>
      )}

      {/* Open-bag switcher (only when more than one bag is open) */}
      {activeBags.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-3 -mx-0.5 px-0.5 [&::-webkit-scrollbar]:hidden">
          {activeBags.map(b => (
            <button
              key={b.id}
              onClick={() => { setSelectedBeanId(b.id); resetOutputs(); }}
              className={`flex-shrink-0 font-sf text-[12px] font-medium px-3 py-1.5 rounded-full transition-colors ${
                b.id === bean.id ? "bg-ink-700 text-white" : "bg-warm-200 text-ink-500"
              }`}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}

      {/* Engine toggle */}
      <Segmented
        options={[{ id: "calc", label: "Calculated" }, { id: "ai", label: "AI dial-in" }]}
        value={engine}
        onChange={(id) => { setEngine(id); setCopied(false); setPushState({ status: "idle", message: "" }); }}
        className="mb-[18px]"
      />

      {engine === "calc" ? (
        <>
          <Readout dose={brew.dose_g} grind={brew.grind.setting} />

          <SpecTiles
            className="mt-[18px]"
            items={[
              { label: "Temp", value: `${brew.temp_f}°` },
              { label: "Ratio", value: brew.ratio_str },
              { label: "Cups", value: fmtCups(cups) },
              { label: "Bloom", value: `${brew.bloom_time_s}s` },
            ]}
          />

          {/* Cups — set how many you're brewing (step 2 of the flow). */}
          <div className="flex items-center justify-between mt-4 px-0.5">
            <span className="font-sf text-[13.5px] font-medium text-ink-600">Cups</span>
            <Stepper value={cups} onChange={changeCups} min={1} max={10} step={0.5} format={fmtCups} />
          </div>

          {/* Mode + weather nudge */}
          <div className="flex items-center justify-between gap-2 mt-3.5 px-0.5">
            <div className="flex items-center gap-1">
              {MODES.map(m => (
                <button
                  key={m.id}
                  onClick={() => changeMode(m.id)}
                  className={`font-sf text-[12px] font-medium px-2 py-1 rounded-md transition-colors ${
                    mode === m.id ? "text-ember-600 bg-ember-500/10" : "text-warm-500"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {(suggestCold || suggestIced) && (
              <button
                onClick={() => changeMode(suggestCold ? "cold" : "iced")}
                className="font-sf text-[11px] text-ember-600 underline decoration-dotted underline-offset-2 flex-shrink-0"
              >
                {suggestCold ? "Cold today?" : "Iced today?"}
              </button>
            )}
          </div>

          {/* Same as yesterday, or adjust (step 3). Default is "same" — one tap
              to brew your usual; open Adjust to nudge from yesterday's cup. */}
          <div className="mt-3.5 px-0.5">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => { changeTune("none"); setAdjustOpen(false); }}
                className={`font-sf text-[12.5px] font-semibold px-3 py-1.5 rounded-full transition-colors ${tune === "none" ? "bg-ink-700 text-white" : "bg-warm-200 text-ink-600"}`}
              >
                Same as yesterday
              </button>
              <button
                onClick={() => setAdjustOpen(o => !o)}
                className={`font-sf text-[12.5px] font-semibold px-3 py-1.5 rounded-full transition-colors flex items-center gap-1 ${tune !== "none" ? "bg-ember-500/15 text-ember-700" : "bg-warm-200 text-ink-600"}`}
              >
                Adjust
                <ChevronRight size={13} className={`transition-transform ${adjustOpen ? "rotate-90" : ""}`} />
              </button>
            </div>
            {adjustOpen && (
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                {TUNES.filter(t => t.id !== "none").map(t => (
                  <button
                    key={t.id}
                    onClick={() => changeTune(t.id)}
                    className={`font-sf text-[12px] font-medium px-2.5 py-1 rounded-full transition-colors ${tune === t.id ? "bg-ink-700 text-white" : "bg-warm-100 text-ink-600 border border-[var(--border-subtle)]"}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
            {brew.tune_note && (
              <div className="mt-2.5 text-[12.5px] text-ember-700 font-medium">Nudged {brew.tune_note}.</div>
            )}
          </div>

          {brew.taste_applied && brew.taste_applied.adjustments.length > 0 && (
            <div className="flex items-center gap-2 mt-3.5 px-0.5 text-[12.5px] text-sage-700 font-medium">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" className="flex-shrink-0"><path d="M12 21s-7-4.35-9.5-8.5C1 9 2.5 5.5 6 5.5c2 0 3.2 1.2 4 2.3.8-1.1 2-2.3 4-2.3 3.5 0 5 3.5 3.5 7C19 16.65 12 21 12 21z"/></svg>
              Tuned for you — {brew.taste_applied.adjustments[0]}
            </div>
          )}

          {/* Compact Fellow-app extras */}
          <div className="flex items-center gap-1.5 flex-wrap mt-3 px-0.5 text-[12px] text-warm-500">
            <span>Fellow app</span>
            <span className="bg-warm-200 rounded-md px-2 py-1 font-medium text-ink-600">
              Bloom {brew.aidenProfile.bloomEnabled ? "on" : "off"}
            </span>
            {brew.aidenProfile.bloomEnabled && (
              <>
                <span className="bg-warm-200 rounded-md px-2 py-1 font-medium text-ink-600 tabular">1:{brew.aidenProfile.bloomRatio}</span>
                <span className="bg-warm-200 rounded-md px-2 py-1 font-medium text-ink-600 tabular">{cToF(brew.aidenProfile.bloomTemperature)}°F</span>
              </>
            )}
          </div>

          {actions(
            formatBrewForFellow({
              dose: brew.dose_g,
              grind: brew.grind.setting,
              cups,
              tempF: brew.temp_f,
              ratioStr: brew.ratio_str,
              bloomEnabled: brew.aidenProfile.bloomEnabled,
              bloomRatio: brew.aidenProfile.bloomRatio,
              bloomTimeS: brew.aidenProfile.bloomDuration,
              bloomTempF: cToF(brew.aidenProfile.bloomTemperature),
            }),
            brew.aidenProfile
          )}
        </>
      ) : (
        <>
          {!store.apiKey ? (
            <div className="bg-warm-100 rounded-2xl p-4 text-[14px] text-ink-500">
              Add your Anthropic API key in Settings to use AI dial-in. Calculated works without it.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-1.5 mb-3">
                {AI_INTENTS.map(o => (
                  <button
                    key={o.id}
                    onClick={() => { setIntent(o.id); resetOutputs(); }}
                    className={`font-sf text-[12.5px] font-medium px-3 py-1.5 rounded-full transition-colors ${
                      intent === o.id ? "bg-ember-500 text-white" : "bg-warm-200 text-ink-600"
                    }`}
                  >
                    <span className="mr-1">{o.icon}</span>{o.label}
                  </button>
                ))}
              </div>

              {!proposal ? (
                <div className="flex items-center gap-2.5">
                  <Stepper value={cups} onChange={changeCups} min={1} max={10} step={0.5} format={fmtCups} />
                  <PrimaryButton onClick={runAi} disabled={aiLoading} className="flex-1">
                    {aiLoading ? <><Loader2 size={16} className="animate-spin" /> Dialing in…</> : <><Sparkles size={16} /> Suggest a recipe</>}
                  </PrimaryButton>
                </div>
              ) : (
                <>
                  <Readout dose={proposal.dose_g} grind={proposal.grind_hint || "—"} />
                  <SpecTiles
                    className="mt-[18px]"
                    items={[
                      { label: "Temp", value: proposal.temp_f != null ? `${proposal.temp_f}°` : "—" },
                      { label: "Ratio", value: proposal.ratio != null ? `1:${proposal.ratio}` : "—" },
                      { label: "Cups", value: fmtCups(cups) },
                      { label: "Bloom", value: proposal.bloom_duration_s != null ? `${proposal.bloom_duration_s}s` : "—" },
                    ]}
                  />
                  {proposal.reasoning && <div className="mt-3.5 px-0.5 text-[12.5px] text-ink-500 leading-relaxed">{proposal.reasoning}</div>}
                  {Array.isArray(proposal.post_brew_tips) && proposal.post_brew_tips.length > 0 && (
                    <ul className="mt-2 px-0.5 space-y-1">
                      {proposal.post_brew_tips.map((t, i) => (
                        <li key={i} className="text-[12px] text-ink-500 flex items-start gap-1.5"><span className="text-ember-500">·</span>{t}</li>
                      ))}
                    </ul>
                  )}
                  {actions(formatProposalForFellow(proposal, cups), proposalToAidenProfile(proposal, bean, cups))}
                </>
              )}
              {aiError && <div className="mt-2 text-[12px] text-ember-600">{aiError}</div>}
            </>
          )}
        </>
      )}

      <MorningWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        greeting={greeting}
        mode={mode}
        onMode={changeMode}
        bean={bean}
        activeBags={activeBags.length ? activeBags : store.beans}
        onBean={selectBean}
        cups={cups}
        onCups={changeCups}
        tune={tune}
        onTune={changeTune}
        brew={brew}
        onPush={handlePush}
        onCopy={handleCopy}
        copied={copied}
        pushState={pushState}
      />
    </div>
  );
}
